<template>
  <section
    :class="[
      'order-section',
      variant === 'usdg' &&
        'tw:bg-white tw:rounded-[20px] tw:shadow-[0_0_0_0.5px_var(--color-usdg-color-border-medium-300)]'
    ]"
  >
    <h3 class="order-section__title">결제수단</h3>
    <!-- 네이버페이 버튼 (위젯 로딩 전에만 표시) -->
    <div
      v-if="!isInsuranceRoute && (isLoadingWidget || !isReadyTossWidget)"
      class="naver-pay-button-wrapper"
    >
      <button class="naver-pay-button">
        <img
          src="/img/social-icon/npay.svg"
          alt="네이버페이"
          class="naver-pay-button__badge"
        />
      </button>
    </div>
    <!-- 로딩 상태 -->
    <div
      v-if="isLoadingWidget && !showRetryUI"
      class="widget-loading"
    >
      <Icons
        path="outline/loading"
        spin
        width="32"
        height="32"
        class="widget-loading__spinner"
      />
      <p class="widget-loading__text">더 많은 결제 수단을 불러오고 있어요</p>
      <p class="widget-loading__text_sub">잠시만 기다려 주세요.</p>
    </div>
    <!-- 재시도 UI (타임아웃 상태)-->
    <div
      v-if="showRetryUI"
      class="widget-loading"
    >
      <IUsdgNormalCircleInfoIcon
        v-if="variant === 'usdg'"
        class="tw:size-6 tw:text-usdg-color-gray-600 mb-12"
      />
      <Icons
        v-else
        path="outline/exclamation-circle"
        width="32"
        height="32"
        class="mb-12"
      />
      <p class="widget-loading__text">다시 시도해 주세요</p>
      <p class="widget-loading__text_sub">결제 수단을 불러오지 못했어요.</p>
      <UsdgCtaButton
        v-if="variant === 'usdg'"
        size="small"
        type="weak"
        color="dark"
        custom-class="widget-loading__retry-cta"
        @click="retryInit"
      >
        다시 시도하기
      </UsdgCtaButton>
      <CtaButton
        v-else
        type="secondary"
        size="md"
        @click="retryInit"
      >
        다시 시도하기
      </CtaButton>
    </div>
    <!-- 결제 UI -->
    <div
      id="paymentMethod"
      :class="{ 'is-loading': isLoadingWidget }"
    />
    <!-- 이용약관 UI -->
    <div
      id="agreement"
      :class="{ 'is-loading': isLoadingWidget }"
    />
  </section>
  <ADivider v-if="variant === 'legacy'" />
</template>

<script setup lang="ts">
import { loadTossPayments, ANONYMOUS } from '@tosspayments/tosspayments-sdk'
import { useSingleButtonDialog } from '@/uComposables/modalControl/useSingleButtonDialog'

const props = withDefaults(
  defineProps<{
    price: number
    userId: string
    simType?: SimType
    itemUid: string // 상품 UID: 옵션ID or 애드온ID
    item?: OrderCheckoutItem | OrderAddonCheckoutItem
    simUsageType?: SimUsageType | 'normal'
    successPath?: string // 결제 성공 콜백 경로 override (기본: /order/callback/success)
    failPath?: string // 결제 실패 콜백 경로 override (기본: /order/callback/fail)
    variant?: 'legacy' | 'usdg'
    selectedCouponInfo?: null
  }>(),
  {
    simType: undefined,
    item: undefined,
    simUsageType: 'normal',
    successPath: '',
    failPath: '',
    variant: 'legacy'
  }
)

const route = useRoute()

// 보험 결제 페이지인지 확인을 위함
const isInsuranceRoute = computed(() => String(route.name ?? '').includes('insurance'))

const config = useRuntimeConfig()
const clientKey = config.public.tossClientKey
// 하나카드 다이렉트 결제 - 위젯 열기 X
const directClientKey = config.public.tossDirectClientKey

const partnerKey = useRoute().params.partner as string | undefined
const baseUrl = useCurrentBaseUrl()
const dayjs = useDayjs()
const { isProcessingPayment } = usePayment()
const { open } = useSingleButtonDialog()

const isReadyTossWidget = ref<boolean>(false) // 결제 위젯 전체가 준비되었는지 여부 (약관 UI까지)
const isLoadingWidget = ref<boolean>(false) // 위젯 로딩 중 여부
const TIMEOUT_DURATION = 15000 // 타임아웃 시간 (ms)
const showRetryUI = ref<boolean>(false) // 재시도 UI 표시 여부
const loadingTimeoutId = ref<NodeJS.Timeout | null>(null) // 타임아웃 타이머 ID
const amount = ref<TossAmount>({ currency: 'KRW', value: props.price })
const widgets = ref<TossPaymentWidgetControl | null>(null)
const agreementWidget = ref<any | null>(null)
const tossPayments = ref<TossPaymentsInstance | null>(null)
const paymentMethodWidget = ref<any | null>(null)
const selectedPaymentMethod = ref<string>('') // 선택한 결제수단
const isSelectNaverPay = computed(() => selectedPaymentMethod.value === 'NAVERPAY')
const isAgreeRequiredTerms = ref<boolean>(true) // 필수 약관 동의 여부
const isUsim = computed(() => (props.simType || '').toLowerCase() === 'usim')
const naverUseCfm = computed(() => {
  // chainId에 따라 포인트적립 방식 계약이 다름. usim: 자동적립, esim: 이용완료일 적립
  return isUsim.value ? '' : dayjs().add(180, 'day').format('YYYYMMDD')
})
const nPayChainId = computed(() => {
  return isUsim.value ? config.public.nPayChainKeyUsim : config.public.nPayChainKeyEsim
})

const HANA_CARD_PAYMENT_METHOD = 'HANA_ilovekorea'
const isHanaCardPayment = computed(() => selectedPaymentMethod.value === HANA_CARD_PAYMENT_METHOD)

// 나라사랑 쿠폰 사용 여부
const HANA_CARD_COUPON_ID = '2FD3A00C-F8AC-F111-9B33-002248F71FAE'
const isHanaCardCoupon = computed(() => {
  return props.selectedCouponInfo?.selectedCoupon?.some(
    (coupon: any) => coupon.id === HANA_CARD_COUPON_ID
  )
})

// 결제 위젯 초기화
const fetchPaymentWidgets = async () => {
  // SDK 초기화
  tossPayments.value = (await loadTossPayments(clientKey)) as unknown as TossPaymentsInstance

  // 회원/비회원 결제
  let customerKey = ANONYMOUS
  if (props.userId) customerKey = props.userId
  widgets.value = tossPayments.value.widgets({ customerKey })
}

// 결제 수단 UI 렌더링
const renderPaymentWidgets = async () => {
  if (!widgets.value) {
    throw new Error('Widgets not initialized')
  }

  if (paymentMethodWidget.value?.destroy) {
    await paymentMethodWidget.value.destroy()
    paymentMethodWidget.value = null
  }

  await widgets.value.setAmount(amount.value)

  const route = useRoute()
  let variantKey = 'DEFAULT'

  if (isHanaCardCoupon.value) {
    variantKey = 'HANA_ilovekorea'
  } else if (route.name?.includes('insurance')) {
    variantKey = 'insurance'
  } else if (route.name === 'airport-taxi-order') {
    variantKey = 'airportKey'
  } else if (route.params.partner === 'kbpay') {
    variantKey = 'kbpay'
  }

  paymentMethodWidget.value = await widgets.value.renderPaymentMethods({
    selector: '#paymentMethod',
    variantKey: variantKey as string
    // variantKey: 'DEFAULT'
  })

  // 초기 선택된 결제수단 조회 및 할당
  const method = await paymentMethodWidget.value.getSelectedPaymentMethod()
  selectedPaymentMethod.value = method.code

  // 결제수단 이벤트 핸들러
  paymentMethodWidget.value.on(
    'paymentMethodSelect',
    (selectedMethod: { code: TossPaymentMethods }) => {
      selectedPaymentMethod.value = selectedMethod.code
    }
  )
}

// 결제 이용 약관 동의 UI 렌더링
const renderAgreementWidgets = async () => {
  if (!widgets.value) {
    throw new Error('Widgets not initialized')
  }
  if (isReadyTossWidget.value) return

  const agreement = (await widgets.value.renderAgreement({
    selector: '#agreement',
    variantKey: 'AGREEMENT'
  })) as unknown as TossAgreementWidget

  agreementWidget.value = agreement

  isReadyTossWidget.value = true

  agreementWidget.value.on('agreementStatusChange', (agreementStatus) => {
    if (agreementStatus.agreedRequiredTerms) {
      isAgreeRequiredTerms.value = true
    } else {
      isAgreeRequiredTerms.value = false
    }
  })
}

const updateAmount = async (newPrice: number) => {
  if (!widgets.value) return

  const newAmount = {
    currency: amount.value.currency,
    value: newPrice
  }
  amount.value.value = newPrice

  try {
    await widgets.value?.setAmount(newAmount)
  } catch (error) {
    console.error('Error setAmount:', error)
  }
}

watch(isHanaCardCoupon, async (isHana, prevIsHana) => {
  if (isHana === prevIsHana) return
  if (!widgets.value) return

  await renderPaymentWidgets()
})

// 토스 결제 요청 핸들러
const requestTossPayment = async (data: PaymentData) => {
  if (!widgets.value) return
  await updateAmount(data.amount)
  const queryString = data.queryString ? `?${data.queryString}` : ''
  const successUrl = props.successPath
    ? `${baseUrl}${props.successPath}${queryString}`
    : partnerKey
      ? `${baseUrl}/partners/${partnerKey}/order/callback/success${queryString}`
      : `${baseUrl}/order/callback/success${queryString}`
  const failUrl = props.failPath
    ? `${baseUrl}${props.failPath}`
    : partnerKey
      ? `${baseUrl}/partners/${partnerKey}/order/callback/fail`
      : `${baseUrl}/order/callback/fail`

  try {
    if (isHanaCardPayment.value) {
      const customerKey = props.userId || ANONYMOUS
      const directTossPayments = await loadTossPayments(directClientKey)
      const payment = directTossPayments.payment({
        customerKey
      })

      if (!payment) return
      await payment.requestPayment({
        method: 'CARD',

        amount: {
          currency: 'KRW',
          value: data.amount
        },

        orderId: data.orderGroupId || data.orderId,
        orderName: data.orderName,

        successUrl,
        failUrl,

        customerName: data.customerName || '',
        customerEmail: data.customerEmail || '',
        customerMobilePhone: data.customerMobilePhone || '',

        card: {
          flowMode: 'DEFAULT'
          // cardCompany: '하나'
        }
      })

      return
    }
    await (widgets.value as TossPaymentWidgetControl).requestPayment({
      orderId: data.orderGroupId || data.orderId,
      orderName: data.orderName,
      successUrl,
      failUrl,
      customerName: data.customerName || '',
      customerEmail: data.customerEmail || '',
      customerMobilePhone: data.customerMobilePhone || null
    })
  } catch (err: unknown) {
    if (typeof err === 'object' && err !== null && 'message' in err) {
      const { code, message } = err as {
        code: string
        message: string
      }

      let errorMsg = message

      if (code === 'NEED_AGREEMENT_WITH_REQUIRED_TERMS') {
        errorMsg = '결제를 위해 약관에 동의해 주세요.'
      }
      open({
        title: errorMsg,
        customClass: 'single-button-warning-dialog'
      })
    }
    isProcessingPayment.value = false
  }
}

const requestNaverPay = (payload: PaymentData) => {
  const { orderId, orderName, amount, queryString } = payload
  const npayQuery = `${queryString}&orderId=${orderId}&amount=${amount}`
  const returnUrl = partnerKey
    ? `${baseUrl}/partners/${partnerKey}/order/callback/npay?${npayQuery}`
    : `${baseUrl}/order/callback/npay?${npayQuery}`

  const payReserveParameters: NaverPayReserveRequest = {
    merchantPayKey: orderId, // 가맹점의 결제번호
    productName: orderName,
    totalPayAmount: amount,
    taxScopeAmount: 0, // 과세 대상 금액
    taxExScopeAmount: amount, // 면세 대상 금액
    returnUrl: returnUrl,
    productItems: [
      {
        categoryType: 'ETC',
        categoryId: 'ETC',
        uid: props.itemUid, // 옵션ID (충전결제인 경우 애드온ID)
        name: orderName,
        count: 1
      }
    ]
  }
  if (!isUsim.value) {
    payReserveParameters.useCfmYmdt = naverUseCfm.value
  }

  try {
    oPay.open(payReserveParameters)

    // 네이버페이는 토스페이먼츠위젯내 결제가 아니라, 이전 결제수단으로 저장되지 않기때문에 초기화
    localStorage.removeItem('@tosspayments/payment-widget-previous-payment-method-id')
  } catch (err: unknown) {
    console.error('네이버페이 결제 요청 중 오류 발생:', err)
    isProcessingPayment.value = false
  }
}

const destroyWidgets = async () => {
  try {
    if (paymentMethodWidget.value?.destroy) {
      await paymentMethodWidget.value.destroy()
    }
    if (agreementWidget.value?.destroy) {
      await agreementWidget.value.destroy()
    }

    // DOM 요소 비우기
    const paymentMethodEl = document.querySelector('#paymentMethod')
    const agreementEl = document.querySelector('#agreement')
    if (paymentMethodEl) {
      paymentMethodEl.innerHTML = ''
    }
    if (agreementEl) {
      agreementEl.innerHTML = ''
    }
  } catch (e) {
    console.warn('Toss 위젯 destroy 중 에러:', e)
  } finally {
    paymentMethodWidget.value = null
    agreementWidget.value = null
    widgets.value = null
    tossPayments.value = null
    isProcessingPayment.value = false
    isReadyTossWidget.value = false
  }

  // 타임아웃 타이머 정리
  if (loadingTimeoutId.value) {
    clearTimeout(loadingTimeoutId.value)
    loadingTimeoutId.value = null
  }
}

/**
 * DOM 요소가 완전히 렌더링될 때까지 기다리는 함수
 *
 * Toss Payments 위젯은 DOM 요소(`#paymentMethod`, `#agreement`)가 실제로 존재하지 않으면
 * renderPaymentMethods/renderAgreement 호출 시 오류가 발생함.
 * CSR 환경에서는 onMounted()가 호출되어도 하위 DOM 요소가 아직 렌더링되지 않았을 수 있으므로,
 * polling 방식으로 해당 요소들이 실제로 생성될 때까지 기다린 후 위젯을 mount 함.
 */
const waitUntilDomReady = () => {
  return new Promise<void>((resolve) => {
    const check = () => {
      const el1 = document.querySelector('#paymentMethod')
      const el2 = document.querySelector('#agreement')
      if (el1 && el2) resolve()
      else setTimeout(check, 30)
    }
    check()
  })
}

// 금액 변경 감지
watch(
  () => props.price,
  async (newPrice) => {
    // 위젯 금액 세팅이 0으로 되는것을 방지
    if (!widgets.value && newPrice > 0) return

    await updateAmount(newPrice)
  },
  { immediate: true }
)

onBeforeRouteLeave(async () => {
  await destroyWidgets()
})

// BFCache 복원 시 위젯 초기화 핸들러
const handlePageshow = async (event: PageTransitionEvent) => {
  if (event.persisted) {
    try {
      await destroyWidgets()
      await initTossWidgets()
    } catch (error) {
      console.error('BFCache 복원 시 위젯 초기화 실패:', error)
      isLoadingWidget.value = false
      showRetryUI.value = true
    }
  }
}

onBeforeUnmount(async () => {
  window.removeEventListener('pageshow', handlePageshow)
  await destroyWidgets()
})

let oPay: unknown = null // 네이버페이 객체

const createNaverPay = async () => {
  oPay = await window.Naver?.Pay.create({
    mode: config.public.nPayMode,
    clientId: config.public.nPayClientId,
    chainId: nPayChainId.value
  })
}

watch(
  () => props.simType,
  async (v) => {
    if (!v) return
    await createNaverPay()
  },
  { immediate: true }
)

const initTossWidgets = async () => {
  isLoadingWidget.value = true
  showRetryUI.value = false
  // 위젯 로딩 시작 시 네이버페이를 기본 선택 상태로 설정
  selectedPaymentMethod.value = 'NAVERPAY'

  // 기존 타임아웃 타이머가 있으면 제거
  if (loadingTimeoutId.value) {
    clearTimeout(loadingTimeoutId.value)
    loadingTimeoutId.value = null
  }

  // 15초 후 재시도 UI 처리
  loadingTimeoutId.value = setTimeout(() => {
    if (isLoadingWidget.value) {
      showRetryUI.value = true
      // 위젯은 destroy하지 않고 로딩 계속 진행
    }
  }, TIMEOUT_DURATION)

  try {
    await fetchPaymentWidgets()
    await waitUntilDomReady()
    await Promise.all([renderPaymentWidgets(), renderAgreementWidgets()])

    // 성공적으로 로딩 완료 시 타임아웃 타이머 제거
    if (loadingTimeoutId.value) {
      clearTimeout(loadingTimeoutId.value)
      loadingTimeoutId.value = null
    }
    // 로딩 완료 처리 (타임아웃이 발생했어도 완료되면 위젯 표시)
    isLoadingWidget.value = false
    showRetryUI.value = false
    // 위젯 로딩 완료 후에는 위젯에서 선택한 결제 수단으로 변경되므로 네이버페이 선택 상태는 위젯에 맡김
    // renderPaymentWidgets에서 위젯의 초기 선택된 결제수단으로 selectedPaymentMethod가 자동으로 변경됨
  } catch (error) {
    console.error('Error initializing Toss widgets:', error)
    // 에러 발생 시에도 타임아웃 타이머 제거
    if (loadingTimeoutId.value) {
      clearTimeout(loadingTimeoutId.value)
      loadingTimeoutId.value = null
    }
    // 에러 발생 시 재시도 UI 표시
    await destroyWidgets()
    isLoadingWidget.value = false
    showRetryUI.value = true
  }
}

// 다시 시도하기 버튼 클릭 핸들러
const retryInit = async () => {
  await destroyWidgets()
  showRetryUI.value = false
  await initTossWidgets()
}

// 네이버페이 버튼은 위젯 로딩 전까지 무조건 선택된 상태로 유지
// 버튼 클릭 핸들러는 제거 (버튼이 disabled 상태이므로 클릭 불가)

onMounted(async () => {
  await initTossWidgets()
  // BFCache 복원 시 위젯 초기화
  window.addEventListener('pageshow', handlePageshow)
})

// 부모컴포넌트에서 사용할 수 있도록 expose
defineExpose({
  itemUid: props.itemUid,
  item: props.item,
  simUsageType: props.simUsageType,
  isReadyTossWidget,
  selectedPaymentMethod,
  isSelectNaverPay,
  isAgreeRequiredTerms,
  requestTossPayment,
  requestNaverPay
})
</script>

<style scoped lang="scss">
.widget-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 282px;

  &__spinner {
    margin-bottom: 28px;
  }

  &__text {
    color: #121417;
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
  }

  &__text_sub {
    color: #767676;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin-bottom: 24px;
  }

  &__retry-btn {
    margin-top: 16px;
  }

  &__retry-cta {
    padding-left: 12px;
    padding-right: 12px;
  }
}

.naver-pay-button-wrapper {
  margin-bottom: 16px;
}

.naver-pay-button {
  width: 100%;
  height: 56px;
  background: white;
  border: 1px solid #06f;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0;
  cursor: pointer;

  &__badge {
    height: 18px;
    width: auto;
  }
}

#paymentMethod,
#agreement {
  &.is-loading {
    opacity: 0;
    pointer-events: none;
    height: 0;
  }
}
</style>
